import type { CSSProperties, KeyboardEvent, MouseEvent, MutableRefObject, ReactNode } from 'react';
import common from '../styles/common.module.css';
import useModalBackdrop from '../hooks/useModalBackdrop';

interface BattlefieldModalProps {
  onBackdropClick?: (e: MouseEvent) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  trapRef?: MutableRefObject<HTMLElement | null>;
  className?: string;
  style?: CSSProperties;
  label: string;
  testId?: string;
  outside?: ReactNode;
  children: ReactNode;
}

/**
 * BattlefieldModal - the backdrop and dialog box the map's dialogs share.
 *
 * A click on the backdrop closes the dialog; a click inside the box is
 * stopped at the backdrop, so it has never reached anything above it, and a
 * click that merely lands outside the box - on `outside`, say - still
 * closes it (useModalBackdrop). The backdrop is `role="presentation"`, so
 * it is not announced as something to press.
 *
 * `onBackdropClick` is called with the event, exactly as each dialog's own
 * backdrop handler was. `onKeyDown` sits on the backdrop so every key
 * pressed inside the dialog reaches it (Escape). `trapRef` is the focus
 * trap's. `outside` is drawn on the backdrop before the box - a hidden
 * file input.
 */
export default function BattlefieldModal({
  onBackdropClick,
  onKeyDown,
  trapRef,
  className = common.modalContent,
  style,
  label,
  testId,
  outside,
  children,
}: BattlefieldModalProps) {
  const backdrop = useModalBackdrop(onBackdropClick, trapRef);
  return (
    <div className={common.modal} onClick={backdrop.onBackdropClick} onKeyDown={onKeyDown} role="presentation">
      {outside}
      <div
        className={className}
        style={style}
        ref={backdrop.boxRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-testid={testId}
      >
        {children}
      </div>
    </div>
  );
}
